export const OSM_CATEGORIES = {
  amenity: {
    label: 'Amenities',
    color: '#3B82F6',
    icon: '🍽️',
    tags: ['restaurant', 'cafe', 'fast_food', 'pub', 'bar', 'bank', 'atm', 'pharmacy', 'hospital', 'fuel', 'charging_station', 'toilets', 'drinking_water'],
  },
  shop: {
    label: 'Shops',
    color: '#10B981',
    icon: '🛒',
    tags: ['supermarket', 'convenience', 'bakery', 'clothes', 'electronics', 'hardware', 'hairdresser'],
  },
  tourism: {
    label: 'Tourism',
    color: '#8B5CF6',
    icon: '🏛️',
    tags: ['hotel', 'hostel', 'camp_site', 'museum', 'artwork', 'viewpoint', 'attraction', 'information'],
  },
  leisure: {
    label: 'Leisure',
    color: '#F59E0B',
    icon: '⚽',
    tags: ['park', 'playground', 'sports_centre', 'pitch', 'swimming_pool', 'fitness_centre'],
  },
  historic: {
    label: 'Historic',
    color: '#D97706',
    icon: '🏰',
    tags: ['castle', 'monument', 'memorial', 'ruins', 'archaeological_site'],
  },
  emergency: {
    label: 'Emergency',
    color: '#EF4444',
    icon: '🚨',
    tags: ['fire_hydrant', 'defibrillator', 'phone', 'assembly_point'],
  },
  office: {
    label: 'Office',
    color: '#6B7280',
    icon: '🏢',
    tags: ['company', 'government', 'ngo'],
  },
  natural: {
    label: 'Natural',
    color: '#059669',
    icon: '🌲',
    tags: ['peak', 'spring', 'tree', 'cave_entrance', 'beach'],
  },
};

export const OSM_CATEGORY_KEYS = Object.keys(OSM_CATEGORIES);

const TAG_ICONS = {
  restaurant: '🍽️', cafe: '☕', fast_food: '🍔', pub: '🍺', bar: '🍸', bank: '🏦', atm: '💳',
  pharmacy: '💊', hospital: '🏥', fuel: '⛽', charging_station: '🔌', toilets: '🚻', drinking_water: '💧',
  supermarket: '🛒', convenience: '🏪', bakery: '🥐', clothes: '👕', electronics: '📱', hardware: '🔧', hairdresser: '💇',
  hotel: '🏨', hostel: '🛏️', camp_site: '⛺', museum: '🏛️', artwork: '🎨', viewpoint: '👀', attraction: '⭐', information: 'ℹ️',
  park: '🌳', playground: '🛝', sports_centre: '🏟️', pitch: '⚽', swimming_pool: '🏊', fitness_centre: '💪',
  castle: '🏰', monument: '🗿', memorial: '🪦', ruins: '🧱', archaeological_site: '🏺',
  fire_hydrant: '🚒', defibrillator: '❤️', phone: '📞', assembly_point: '🚩',
  company: '🏢', government: '🏛️', ngo: '🤝',
  peak: '⛰️', spring: '💧', tree: '🌲', cave_entrance: '🕳️', beach: '🏖️',
};

export function generateOverpassQuery(bbox, activeCategories = OSM_CATEGORY_KEYS) {
  const [west, south, east, north] = normalizeBbox(bbox);
  const selected = new Set(activeCategories);
  const clauses = [];
  for (const category of OSM_CATEGORY_KEYS) {
    if (!selected.has(category)) continue;
    const values = OSM_CATEGORIES[category].tags.map((tag) => tag.replace(/["\\]/g, '\\$&')).join('|');
    clauses.push(`nwr["${category}"~"^(${values})$"](${south},${west},${north},${east});`);
  }
  if (!clauses.length) return '[out:json][timeout:25];out body;';
  return `[out:json][timeout:25];(${clauses.join('')});out center tags;`;
}

export function normalizeBbox(bbox) {
  if (!Array.isArray(bbox) || bbox.length !== 4) throw new Error('bbox must be [west,south,east,north]');
  let [west, south, east, north] = bbox.map(Number);
  if ([west, south, east, north].some((value) => !Number.isFinite(value))) {
    throw new Error('bbox contains invalid coordinates');
  }
  west = Math.max(-180, Math.min(180, west));
  east = Math.max(-180, Math.min(180, east));
  south = Math.max(-90, Math.min(90, south));
  north = Math.max(-90, Math.min(90, north));
  if (west === east) {
    west = Math.max(-180, west - 0.01);
    east = Math.min(180, east + 0.01);
  }
  if (south === north) {
    south = Math.max(-90, south - 0.01);
    north = Math.min(90, north + 0.01);
  }
  if (south > north) [south, north] = [north, south];
  // Antimeridian: keep as-is; Overpass/local query handle wrap separately.
  if (west > east) {
    return [west, south, east, north];
  }
  return [west, south, east, north];
}

export function classifyOSM(tags = {}) {
  for (const category of OSM_CATEGORY_KEYS) {
    const value = tags[category];
    if (value && OSM_CATEGORIES[category].tags.includes(value)) return category;
  }
  return 'amenity';
}

export function markerColor(category) {
  return OSM_CATEGORIES[category]?.color || OSM_CATEGORIES.amenity.color;
}

export function markerIcon(categoryOrValue) {
  if (TAG_ICONS[categoryOrValue]) return TAG_ICONS[categoryOrValue];
  return OSM_CATEGORIES[categoryOrValue]?.icon || '📍';
}
